import React, { useEffect, useMemo, useState } from "react";
import Layout from "../../Component/Layout"; // adjust path to match your project structure
import axios from "axios";
import Select from "react-select";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MASTER_API, PURCHASE_API } from "../../apiConfig"; // adjust path if apiConfig lives elsewhere

// ── compact react-select styles (same pattern used in GST screen) ──
const rsCompact = {
  control: (base) => ({
    ...base,
    minHeight: "32px",
    height: "32px",
    fontSize: "13px",
    borderColor: "#ced4da",
    boxShadow: "none",
    "&:hover": { borderColor: "#86b7fe" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: "32px" }),
  menu: (base) => ({ ...base, zIndex: 99999, fontSize: "13px" }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const currentYear = new Date().getFullYear();
// A reasonable, non-hardcoded-to-business-meaning range of selectable years
const YEARS = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

// Country master rows may expose the currency under different field names
// depending on how the master was set up. This picks whichever is present
// instead of hardcoding any currency value.
const getCountryCurrency = (countryObj) =>
  countryObj?.currency_code ||
  countryObj?.currency ||
  countryObj?.curr_code ||
  countryObj?.currency_name ||
  "";

const emptyForm = () => ({
  month: "",
  year: "",
  from_country: "",
  from_currency: "",
  to_country: "",
  to_currency: "",
  exchange_rate: "",
});

const ExchangeRate = () => {
  const [countries, setCountries] = useState([]);
  const [list, setList] = useState([]);

  // filters
  const [filterFromCountry, setFilterFromCountry] = useState("");
  const [filterToCountry, setFilterToCountry] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  // ================= INITIAL LOAD =================
  useEffect(() => {
    refreshList();

    axios.get(`${MASTER_API}/configuration/country`)
      .then(res => setCountries(Array.isArray(res.data) ? res.data : res.data?.data || []))
      .catch(err => console.error("Country error:", err));
  }, []);

  const refreshList = (filters = {}) => {
    axios.get(`${PURCHASE_API}/exchange-rate-config`, { params: filters })
      .then(res => setList(Array.isArray(res.data) ? res.data : res.data?.data || []))
      .catch(err => console.error("Exchange rate list error:", err));
  };

  const countryOptions = useMemo(
    () => countries.map(c => ({ value: c.name || c, label: c.name || c, raw: c })),
    [countries]
  );

  // ================= FILTER (auto-applied on change) =================
  const applyFilters = (overrides = {}) => {
    const next = {
      from_country: filterFromCountry,
      to_country: filterToCountry,
      month: filterMonth,
      year: filterYear,
      ...overrides,
    };
    refreshList({
      from_country: next.from_country || undefined,
      to_country: next.to_country || undefined,
      month: next.month || undefined,
      year: next.year || undefined,
    });
  };

  // ================= MODAL OPEN/CLOSE =================
  const openCreate = () => {
    setForm(emptyForm());
    setEditingId(null);
    setShowModal(true);
  };

  // "View" opens the same editable modal as "Edit" — there's no separate
  // read-only mode, since anything you can view here you can also correct.
  const openView = (row) => {
    setForm({
      month: row.month,
      year: row.year,
      from_country: row.from_country,
      from_currency: row.from_currency,
      to_country: row.to_country,
      to_currency: row.to_currency,
      exchange_rate: String(row.exchange_rate),
    });
    setEditingId(row.id);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(emptyForm());
    setEditingId(null);
  };

  // ================= FORM FIELD HANDLERS =================
  const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleFromCountrySelect = (opt) => {
    setField("from_country", opt?.value || "");
    setField("from_currency", opt ? getCountryCurrency(opt.raw) : "");
  };

  const handleToCountrySelect = (opt) => {
    setField("to_country", opt?.value || "");
    setField("to_currency", opt ? getCountryCurrency(opt.raw) : "");
  };

  // ================= SAVE =================
  const handleSave = async () => {
    if (!form.month) return toast.warning("Month is mandatory");
    if (!form.year) return toast.warning("Year is mandatory");
    if (!form.from_country) return toast.warning("From Country is mandatory");
    if (!form.to_country) return toast.warning("To Country is mandatory");
    if (form.from_country === form.to_country) return toast.warning("From Country and To Country cannot be the same");
    if (form.exchange_rate === "" || Number.isNaN(Number(form.exchange_rate))) {
      return toast.warning("Exchange Rate is mandatory");
    }

    const payload = { ...form, exchange_rate: Number(form.exchange_rate) };

    try {
      if (editingId) {
        await axios.put(`${PURCHASE_API}/exchange-rate-config/${editingId}`, payload);
        toast.success("Exchange rate updated");
      } else {
        await axios.post(`${PURCHASE_API}/exchange-rate-config`, payload);
        toast.success("Exchange rate saved");
      }
      closeModal();
      applyFilters();
    } catch (err) {
      console.error("Save exchange rate error:", err);
      toast.error(err?.response?.data?.error || "Failed to save exchange rate");
    }
  };

  // ================= DELETE =================
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this exchange rate?")) return;
    try {
      await axios.delete(`${PURCHASE_API}/exchange-rate-config/${id}`);
      toast.success("Deleted");
      applyFilters();
    } catch (err) {
      console.error("Delete exchange rate error:", err);
      toast.error("Failed to delete");
    }
  };

  return (
    <Layout>
      <style>{`
        .er-wrapper { padding: 32px; font-family: Inter, Poppins, sans-serif; }
        .er-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; }
        .er-header h1 { font-size: 24px; font-weight: 700; color: #0b1f4d; margin: 0 0 6px 0; }
        .er-header p { font-size: 14px; color: #6b7280; margin: 0; }
        .er-create-btn {
          background: #0b1f4d; color: #fff; border: none; border-radius: 10px;
          padding: 10px 18px; font-size: 13px; font-weight: 600; cursor: pointer;
          box-shadow: 0 1px 3px rgba(16,24,40,0.15);
        }
        .er-create-btn:hover { background: #132a63; }

        .er-filter-card {
          background: #ffffff; border: 1px solid #e5e7eb; border-radius: 16px;
          padding: 20px; margin-bottom: 20px; box-shadow: 0 1px 3px rgba(16,24,40,0.06);
        }
        .er-filter-label { font-size: 12px; font-weight: 600; color: #334155; margin-bottom: 4px; display: block; }

        .er-table-card {
          background: #ffffff; border: 1px solid #e5e7eb; border-radius: 16px;
          padding: 20px; box-shadow: 0 1px 3px rgba(16,24,40,0.06); overflow-x: auto;
        }
        .er-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .er-table th {
          text-align: left; background: #f8fafc; font-weight: 600; color: #1e293b;
          padding: 10px 12px; border-bottom: 1px solid #e5e7eb; white-space: nowrap;
        }
        .er-table td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; color: #334155; vertical-align: middle; white-space: nowrap; }
        .er-currency-chip {
          display: inline-block; background: #eef2ff; color: #4338ca; font-size: 11px;
          font-weight: 600; padding: 2px 8px; border-radius: 999px;
        }

        .er-modal-backdrop {
          position: fixed; inset: 0; background: rgba(15,23,42,0.45);
          display: flex; align-items: center; justify-content: center; z-index: 9998;
        }
        .er-modal {
          background: #fff; border-radius: 16px; width: 480px; max-width: 92vw;
          max-height: 88vh; overflow-y: auto; box-shadow: 0 20px 45px rgba(15,23,42,0.25);
        }
        .er-modal-header {
          padding: 16px 20px; border-bottom: 1px solid #e5e7eb; display: flex;
          justify-content: space-between; align-items: center;
        }
        .er-modal-title { font-size: 16px; font-weight: 700; color: #0b1f4d; }
        .er-close-btn { background: none; border: none; font-size: 16px; cursor: pointer; color: #6b7280; }
        .er-modal-body { padding: 20px; }
        .er-field-label { font-size: 12px; font-weight: 600; color: #334155; margin-bottom: 4px; display: block; }
        .er-field-row { display: flex; gap: 14px; margin-bottom: 14px; }
        .er-field-row > div { flex: 1; }
        .er-currency-readonly {
          height: 32px; display: flex; align-items: center; padding: 0 10px;
          background: #f8fafc; border: 1px solid #e5e7eb; border-radius: 6px;
          font-size: 13px; color: #334155;
        }
        .er-modal-footer {
          padding: 14px 20px; border-top: 1px solid #e5e7eb; display: flex;
          justify-content: flex-end; gap: 10px;
        }
      `}</style>

      <div className="er-wrapper">
        <div className="er-header">
          <div>
            <h1>Exchange Rate</h1>
            <p>Configure and maintain currency exchange rates by month and country pair</p>
          </div>
          <button className="er-create-btn" onClick={openCreate}>+ Create</button>
        </div>

        {/* ================= FILTERS ================= */}
        <div className="er-filter-card">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="er-filter-label">From Country</label>
              <Select
                styles={rsCompact}
                placeholder="Select From Country"
                isClearable
                options={countryOptions}
                value={filterFromCountry ? { value: filterFromCountry, label: filterFromCountry } : null}
                onChange={(opt) => {
                  const value = opt?.value || "";
                  setFilterFromCountry(value);
                  applyFilters({ from_country: value });
                }}
              />
            </div>
            <div className="col-md-3">
              <label className="er-filter-label">To Country</label>
              <Select
                styles={rsCompact}
                placeholder="Select To Country"
                isClearable
                options={countryOptions}
                value={filterToCountry ? { value: filterToCountry, label: filterToCountry } : null}
                onChange={(opt) => {
                  const value = opt?.value || "";
                  setFilterToCountry(value);
                  applyFilters({ to_country: value });
                }}
              />
            </div>
            <div className="col-md-3">
              <label className="er-filter-label">Month</label>
              <Select
                styles={rsCompact}
                placeholder="Month"
                isClearable
                options={MONTHS.map(m => ({ value: m, label: m }))}
                value={filterMonth ? { value: filterMonth, label: filterMonth } : null}
                onChange={(opt) => {
                  const value = opt?.value || "";
                  setFilterMonth(value);
                  applyFilters({ month: value });
                }}
              />
            </div>
            <div className="col-md-3">
              <label className="er-filter-label">Year</label>
              <Select
                styles={rsCompact}
                placeholder="Year"
                isClearable
                options={YEARS.map(y => ({ value: y, label: String(y) }))}
                value={filterYear ? { value: filterYear, label: String(filterYear) } : null}
                onChange={(opt) => {
                  const value = opt?.value || "";
                  setFilterYear(value);
                  applyFilters({ year: value });
                }}
              />
            </div>
          </div>
        </div>

        {/* ================= TABLE ================= */}
        <div className="er-table-card">
          <table className="er-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Year</th>
                <th>From Country</th>
                <th>Currency</th>
                <th>To Country</th>
                <th>Currency</th>
                <th>Exchange Rate</th>
                <th style={{ textAlign: "center" }}></th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: "center", padding: 20, color: "#94a3b8" }}>
                    No exchange rates added yet
                  </td>
                </tr>
              ) : (
                list.map((row) => (
                  <tr key={row.id}>
                    <td>{row.month}</td>
                    <td>{row.year}</td>
                    <td>{row.from_country}</td>
                    <td>{row.from_currency ? <span className="er-currency-chip">{row.from_currency}</span> : "—"}</td>
                    <td>{row.to_country}</td>
                    <td>{row.to_currency ? <span className="er-currency-chip">{row.to_currency}</span> : "—"}</td>
                    <td>{row.exchange_rate}</td>
                    <td style={{ textAlign: "center" }}>
                      <button className="btn btn-outline-primary btn-sm py-0 me-2" style={{ fontSize: 12 }} onClick={() => openView(row)}>
                        View
                      </button>
                      <button className="btn btn-outline-danger btn-sm py-0" style={{ fontSize: 12 }} onClick={() => handleDelete(row.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ================= CREATE / VIEW / EDIT MODAL ================= */}
      {showModal && (
        <div className="er-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="er-modal">
            <div className="er-modal-header">
              <div className="er-modal-title">
                {editingId ? "Exchange Rate" : "Create Exchange Rate"}
              </div>
              <button className="er-close-btn" onClick={closeModal}>✕</button>
            </div>

            <div className="er-modal-body">
              <div className="er-field-row">
                <div>
                  <label className="er-field-label">Month *</label>
                  <Select
                    styles={rsCompact}
                    placeholder="Select Month"
                    options={MONTHS.map(m => ({ value: m, label: m }))}
                    value={form.month ? { value: form.month, label: form.month } : null}
                    onChange={(opt) => setField("month", opt?.value || "")}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
                <div>
                  <label className="er-field-label">Year *</label>
                  <Select
                    styles={rsCompact}
                    placeholder="Select Year"
                    options={YEARS.map(y => ({ value: y, label: String(y) }))}
                    value={form.year ? { value: form.year, label: String(form.year) } : null}
                    onChange={(opt) => setField("year", opt?.value || "")}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
              </div>

              <div className="er-field-row">
                <div>
                  <label className="er-field-label">From Country *</label>
                  <Select
                    styles={rsCompact}
                    placeholder="Select From Country"
                    options={countryOptions}
                    value={form.from_country ? { value: form.from_country, label: form.from_country } : null}
                    onChange={handleFromCountrySelect}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
                <div>
                  <label className="er-field-label">Currency</label>
                  <div className="er-currency-readonly">{form.from_currency || "—"}</div>
                </div>
              </div>

              <div className="er-field-row">
                <div>
                  <label className="er-field-label">To Country *</label>
                  <Select
                    styles={rsCompact}
                    placeholder="Select To Country"
                    options={countryOptions}
                    value={form.to_country ? { value: form.to_country, label: form.to_country } : null}
                    onChange={handleToCountrySelect}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>
                <div>
                  <label className="er-field-label">Currency</label>
                  <div className="er-currency-readonly">{form.to_currency || "—"}</div>
                </div>
              </div>

              <div>
                <label className="er-field-label">Exchange Rate *</label>
                <input
                  type="number"
                  step="0.000001"
                  className="form-control"
                  style={{ fontSize: 13, height: 32, padding: "4px 8px" }}
                  placeholder="e.g. 0.011"
                  value={form.exchange_rate}
                  onChange={(e) => setField("exchange_rate", e.target.value)}
                />
                {form.from_currency && form.to_currency && (
                  <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>
                    1 {form.from_currency} = {form.exchange_rate || "?"} {form.to_currency}
                  </div>
                )}
              </div>
            </div>

            <div className="er-modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={closeModal}>Cancel</button>
              <button className="btn btn-dark btn-sm" onClick={handleSave}>Save</button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Layout>
  );
};

export default ExchangeRate;